import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AuthImage from "@/components/AuthImage";
import { Send } from "lucide-react";

export const SupportChat = ({ threadId, subtitle, heightClass = "h-[70vh]", hideHeader = false }) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const bottomRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/plans/${threadId}/messages`);
      setMessages(data);
    } catch { /* noop */ }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [threadId]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    const body = text;
    setText("");
    try {
      const { data } = await api.post(`/plans/${threadId}/messages`, { text: body });
      setMessages((m) => [...m, data]);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="support-chat" className={`sc-card flex flex-col ${heightClass}`}>
      {!hideHeader && (
        <div className="px-6 py-4 border-b border-[#E3DED2]">
          <h2 className="font-serif text-2xl text-[#26130E]">Conversa</h2>
          {subtitle && <p className="text-xs text-[#6E5F58]">{subtitle}</p>}
        </div>
      )}
      <div data-testid="support-chat-messages" className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
        {messages.length === 0 && <p className="text-center text-[#6E5F58] text-sm">Ainda não há mensagens. Escreve a primeira.</p>}
        {messages.map((m) => {
          const mine = m.user_id === user.id;
          return (
            <div key={m.id} className={`flex gap-3 ${mine ? "flex-row-reverse" : ""}`}>
              <AuthImage path={m.photo_path} fallbackName={m.user_name} className="w-9 h-9 rounded-full object-cover flex-shrink-0 text-xs" />
              <div className={`max-w-[75%] ${mine ? "items-end text-right" : ""} flex flex-col`}>
                <span className="text-xs text-[#6E5F58] mb-1">{m.user_name}{m.is_admin ? " · Admin" : ""}</span>
                <div className={`rounded-2xl px-4 py-2.5 text-sm ${mine ? "bg-[#48020C] text-[#FCFCFA]" : m.is_admin ? "bg-[#F7EEC7] text-[#26130E] border border-[#48020C]/40" : "bg-[#FCFCFA] text-[#1A1412] border border-[#E3DED2]"}`}>
                  {m.text}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>
      <form onSubmit={send} className="p-4 border-t border-[#E3DED2] flex gap-3">
        <input data-testid="support-chat-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Escreve uma mensagem…"
          className="flex-1 bg-[#FCFCFA] border border-[#E3DED2] rounded-full px-5 py-3 text-sm focus:outline-none focus:border-[#48020C]" />
        <button data-testid="support-chat-send-btn" type="submit" className="bg-[#48020C] hover:bg-[#26130E] text-[#F7EEC7] rounded-full w-12 h-12 flex items-center justify-center transition-colors flex-shrink-0">
          <Send className="w-5 h-5" />
        </button>
      </form>
    </div>
  );
};
